import { styled } from '@linaria/react';
import { useCallback, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getDatasetColor } from '../artifact-colors';
import { useIsExporting } from '../artifact-export-context';
import {
  X_KEY,
  buildChartRows,
  buildSeries,
  formatAxisTick,
  formatBarLabel,
  needsAngledTicks,
  truncateAxisLabel,
  truncateAxisTitle,
} from './freeform-chart-artifact.helpers';
import { CHART_HEIGHT, ChartTooltip, EmptyState, SeriesLegend } from './freeform-chart-shared';
import type { FreeformChartConfig, FreeformChartData } from './freeform-chart-artifact.type';

const MAX_LABELLED_BARS = 60;

// Angled labels sit in parallel diagonal lanes needing ~23px of horizontal
// pitch each; past this many categories a narrow panel runs out, so hand
// collision-thinning back to recharts instead of forcing every tick.
const MAX_FORCED_ANGLED_TICKS = 24;

/**
 * Rounds a positive magnitude up to a clean half-power step (…, 15, 20, 25,
 * 30, …) so a computed axis bound lands on a readable tick value.
 *
 * @param {number} value - The magnitude to round; must be positive.
 * @returns {number} The rounded magnitude.
 */
function niceMagnitude(value: number): number {
  const step = 10 ** Math.floor(Math.log10(value)) / 2;
  return Math.ceil(value / step) * step;
}

/**
 * Renders the line, area, bar, and stacked-bar freeform chart types from one
 * columnar payload: the first column is the category axis, each remaining
 * column one series, with a clickable legend toggling series visibility.
 *
 * @param props.payload - The validated columnar `{columns, rows}` data.
 * @param props.config - Presentation config (chart type, axis labels).
 * @returns The chart with its legend.
 */
function FreeformXYChart({ payload, config }: { payload: FreeformChartData; config: FreeformChartConfig }) {
  const isExporting = useIsExporting();
  const series = useMemo(() => buildSeries(payload.columns), [payload.columns]);
  const chartRows = useMemo(() => buildChartRows(payload.columns, payload.rows), [payload]);

  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(series.map((entry) => [entry.key, true])),
  );

  const toggleSeries = useCallback((key: string) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (series.length === 0) {
    return <EmptyState>No data to display</EmptyState>;
  }

  const stacked = config.chartType === 'stacked_bar';
  const visibleKeys = series.filter((entry) => visibleSeries[entry.key] !== false).map((entry) => entry.key);
  // Past this many rendered bars the per-bar labels are narrower than their own
  // text, so they turn into noise and needlessly inflate the exported SVG.
  const showBarLabels = chartRows.length * Math.max(visibleKeys.length, 1) <= MAX_LABELLED_BARS;

  // Long category names can't sit horizontally — recharts silently drops
  // colliding labels, orphaning one mid-axis. Angle every tick instead.
  const angledTicks = needsAngledTicks(chartRows.map((row) => String(row[X_KEY] ?? '')));

  // Only visible series drive the negative handling below — recharts
  // recomputes the domain from what's shown, so a hidden series must not
  // affect it either.
  const seriesValues = chartRows.flatMap((row) =>
    series
      .filter((entry) => visibleSeries[entry.key] !== false)
      .map((entry) => row[entry.key])
      .filter((value): value is number => typeof value === 'number'),
  );
  const hasNegative = seriesValues.some((value) => value < 0);
  // With negatives on a baseline-filled chart the domain needs two guarantees
  // recharts' auto domain doesn't give: zero stays inside it (all-negative
  // data would otherwise anchor bars at an arbitrary top and their lengths
  // would lie), and the floor sits ~8% below the deepest bar so its value
  // label fits under the tip without reaching the tick band. Both bounds
  // snap to a clean shared step so the ticks stay round with zero among
  // them. Lines are position-encoded and keep the tighter auto domain.
  let yDomain: [number, number] | undefined;
  let yTicks: number[] | undefined;
  if (config.chartType !== 'line' && hasNegative) {
    const minValue = Math.min(...seriesValues);
    const maxValue = Math.max(...seriesValues);
    const rawTop = maxValue <= 0 ? 0 : maxValue * 1.05;
    const rawFloor = minValue - (rawTop - minValue) * 0.08;
    const step = niceMagnitude((rawTop - rawFloor) / 4);
    const floor = -Math.ceil(Math.abs(rawFloor) / step) * step;
    const top = rawTop <= 0 ? 0 : Math.ceil(rawTop / step) * step;
    yDomain = [floor, top];
    const tickCount = Math.round((top - floor) / step);
    yTicks = Array.from({ length: tickCount + 1 }, (_, index) => floor + index * step);
  }

  const margin = {
    top: 12,
    right: 8,
    left: angledTicks ? 24 : 0,
    bottom: config.xAxisLabel ? 14 : 4,
  };
  const xAxis = (
    <XAxis
      dataKey={X_KEY}
      tick={angledTicks ? <AngledAxisTick /> : { fontSize: 11 }}
      axisLine={!hasNegative}
      tickLine={false}
      interval={angledTicks && chartRows.length <= MAX_FORCED_ANGLED_TICKS ? 0 : undefined}
      // A max-width rotated tick descends ~80px, so anything less leaves the
      // axis label overlapping the tick tails.
      height={angledTicks ? 104 : undefined}
      label={
        config.xAxisLabel
          ? {
              value: config.xAxisLabel,
              position: 'insideBottom',
              offset: angledTicks ? 2 : -10,
              style: { fontSize: 11, fill: 'var(--ink-lighter)' },
              content: HorizontalAxisTitle,
            }
          : undefined
      }
    />
  );
  const yAxis = (
    <YAxis
      tick={{ fontSize: 11 }}
      tickLine={false}
      axisLine={false}
      tickFormatter={formatAxisTick}
      domain={yDomain}
      ticks={yTicks}
      label={
        config.yAxisLabel
          ? {
              value: config.yAxisLabel,
              angle: -90,
              position: 'insideLeft',
              style: { fontSize: 11, fill: 'var(--ink-lighter)' },
              offset: 10,
              content: VerticalAxisTitle,
            }
          : undefined
      }
    />
  );
  const grid = <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />;
  const zeroLine = hasNegative ? <ReferenceLine y={0} stroke="#666" /> : null;
  // Area and stacked bar sum into a whole, so the tooltip shows a stack total.
  const isStacked = stacked || config.chartType === 'area';
  const tooltip = <Tooltip content={(props) => <ChartTooltip {...props} showTotal={isStacked} />} />;

  return (
    <>
      <ChartBox $overflowVisible={angledTicks}>
        <ResponsiveContainer width="100%" height="100%">
          {config.chartType === 'line' ? (
            <LineChart data={chartRows} margin={margin}>
              {grid}
              {xAxis}
              {yAxis}
              {zeroLine}
              {tooltip}
              {series.map((entry, idx) => {
                const color = getDatasetColor(idx);
                return (
                  <Line
                    key={entry.key}
                    // hide instead of unmount (also Area/Bar below): recharts
                    // slots series by mount order, so a remounted series would
                    // lose its place — bar group position, stack layer.
                    hide={visibleSeries[entry.key] === false}
                    type="monotone"
                    dataKey={entry.key}
                    name={entry.name}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ r: 3, fill: color }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                    isAnimationActive={!isExporting}
                  />
                );
              })}
            </LineChart>
          ) : config.chartType === 'area' ? (
            <AreaChart data={chartRows} margin={margin}>
              {grid}
              {xAxis}
              {yAxis}
              {zeroLine}
              {tooltip}
              {series.map((entry, idx) => {
                const color = getDatasetColor(idx);
                return (
                  <Area
                    key={entry.key}
                    hide={visibleSeries[entry.key] === false}
                    type="monotone"
                    dataKey={entry.key}
                    name={entry.name}
                    stackId="stack"
                    stroke={color}
                    strokeWidth={2}
                    fill={color}
                    fillOpacity={0.2}
                    dot={{ r: 3, fill: color }}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                    isAnimationActive={!isExporting}
                  />
                );
              })}
            </AreaChart>
          ) : (
            <BarChart data={chartRows} margin={margin}>
              {grid}
              {xAxis}
              {yAxis}
              {zeroLine}
              {tooltip}
              {series.map((entry, idx) => {
                const color = getDatasetColor(idx);
                return (
                  <Bar
                    key={entry.key}
                    hide={visibleSeries[entry.key] === false}
                    dataKey={entry.key}
                    name={entry.name}
                    stackId={stacked ? 'stack' : undefined}
                    fill={color}
                    radius={stacked ? undefined : [3, 3, 0, 0]}
                    isAnimationActive={!isExporting}
                  >
                    {showBarLabels &&
                      (stacked ? (
                        <LabelList
                          dataKey={entry.key}
                          position="center"
                          formatter={formatBarLabel}
                          style={{ fontSize: 10, fill: '#fff' }}
                        />
                      ) : (
                        <LabelList dataKey={entry.key} content={BarValueLabel} />
                      ))}
                  </Bar>
                );
              })}
            </BarChart>
          )}
        </ResponsiveContainer>
      </ChartBox>
      <SeriesLegend
        items={series.map((entry, idx) => ({ id: entry.key, name: entry.name, color: getDatasetColor(idx) }))}
        visible={visibleSeries}
        onToggle={toggleSeries}
      />
    </>
  );
}

type BarValueLabelProps = {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: unknown;
};

// Draws the value at the bar's outer end — above the top for positive bars,
// below the tip for negative ones (whose rects recharts hands over
// bottom-anchored with a negative height). The padded yDomain floor
// guarantees room under the deepest tip, so the label never reaches the
// tick band.
function BarValueLabel(props: unknown) {
  const { x, y, width, height, value } = props as BarValueLabelProps;
  if (typeof x !== 'number' || typeof y !== 'number' || typeof width !== 'number' || typeof value !== 'number') {
    return null;
  }
  const barHeight = typeof height === 'number' ? height : 0;
  const labelY = value < 0 ? Math.max(y, y + barHeight) + 12 : Math.min(y, y + barHeight) - 4;
  return (
    <text x={x + width / 2} y={labelY} textAnchor="middle" fontSize={10} fill="var(--ink-lighter)">
      {formatBarLabel(value)}
    </text>
  );
}

function AngledAxisTick({ x, y, payload }: { x?: number; y?: number; payload?: { value?: unknown } }) {
  // Anchoring the end of the text at its own tick keeps each label visually
  // attached to its bar/point; the truncated tail stays hover-accessible via
  // the chart tooltip.
  return (
    <g transform={`translate(${x ?? 0},${y ?? 0})`}>
      <text dy={10} textAnchor="end" transform="rotate(-35)" fontSize={11} fill="var(--ink-base)">
        {truncateAxisLabel(payload?.value)}
      </text>
    </g>
  );
}

type AxisTitleProps = {
  // recharts' label viewBox is a cartesian/polar union; these renderers only
  // ever mount on cartesian axes, so narrow it locally.
  viewBox?: { x?: number; y?: number; width?: number; height?: number };
  value?: unknown;
  offset?: number;
};

// Custom label renderers so long axis titles truncate instead of overflowing
// the card; the SVG <title> child restores the full text on hover.
function VerticalAxisTitle(props: unknown) {
  const { viewBox, value } = props as AxisTitleProps;
  const centerX = (viewBox?.x ?? 0) + 12;
  const centerY = (viewBox?.y ?? 0) + (viewBox?.height ?? 0) / 2;
  const full = String(value ?? '');
  return (
    <text
      x={centerX}
      y={centerY}
      transform={`rotate(-90, ${centerX}, ${centerY})`}
      textAnchor="middle"
      fontSize={11}
      fill="var(--ink-lighter)"
    >
      <title>{full}</title>
      {truncateAxisTitle(full)}
    </text>
  );
}

function HorizontalAxisTitle(props: unknown) {
  const { viewBox, value, offset } = props as AxisTitleProps;
  const centerX = (viewBox?.x ?? 0) + (viewBox?.width ?? 0) / 2;
  const baselineY = (viewBox?.y ?? 0) + (viewBox?.height ?? 0) - (offset ?? 0);
  const full = String(value ?? '');
  return (
    <text x={centerX} y={baselineY} textAnchor="middle" fontSize={11} fill="var(--ink-lighter)">
      <title>{full}</title>
      {truncateAxisTitle(full)}
    </text>
  );
}

// Angled category ticks anchor their end at the tick, so the leftmost label's
// tail extends past the SVG edge — let it spill into the card padding instead
// of clipping mid-word. Charts without angled ticks keep the default clip.
const ChartBox = styled.div<{ $overflowVisible: boolean }>`
  width: 100%;
  height: ${CHART_HEIGHT}px;

  svg {
    overflow: ${({ $overflowVisible }) => ($overflowVisible ? 'visible' : 'hidden')};
  }
`;

export { FreeformXYChart };
